import { useCallback, useEffect } from 'react';

import { getStorageValue, setStorageValue } from 'App/Helpers';

/**
 * Subscribes save and load handlers to the progress slots.
 *
 * @param {object} options
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 * @param {Function} options.onSave
 * @param {Function} options.onLoad
 */
const useGameProgressSlots = ({ addEventListener, removeEventListener, onSave, onLoad }) => {
    useEffect(() => {
        addEventListener('progress.save', onSave);

        return () => removeEventListener('progress.save', onSave);
    }, [addEventListener, removeEventListener, onSave]);

    useEffect(() => {
        addEventListener('progress.load', onLoad);

        return () => removeEventListener('progress.load', onLoad);
    }, [addEventListener, removeEventListener, onLoad]);
};

/**
 * Saves and restores KickAndBoom progress, one entry per key.
 *
 * Save receives `(key, data)` and stores `game.progress.<key>`.
 * Load receives `(key)` and replies on `progress.loadResult` with
 * `${key}\u001F${data}`.
 *
 * Bacanagol's single progress string uses {@link useGameProgressValue}.
 *
 * @param {object} options
 * @param {string} options.storageKey - Local storage namespace for this landing.
 * @param {Function} options.sendMessage - Bridged Unity sendMessage.
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 */
export const useGameProgress = ({
    storageKey,
    sendMessage,
    addEventListener,
    removeEventListener,
}) => {
    const handleSaveGameProgress = useCallback(
        (gameSaveKey, gameSaveData) => {
            setStorageValue(storageKey, `game.progress.${gameSaveKey}`, gameSaveData);
        },
        [storageKey]
    );

    const handleSendGameProgress = useCallback(
        (gameSaveKey) => {
            const gameProgress = getStorageValue(storageKey, `game.progress.${gameSaveKey}`) || '';
            const sendValue = `${gameSaveKey}\u001F${gameProgress}`;

            sendMessage('progress.loadResult', sendValue);
        },
        [sendMessage, storageKey]
    );

    useGameProgressSlots({
        addEventListener,
        removeEventListener,
        onSave: handleSaveGameProgress,
        onLoad: handleSendGameProgress,
    });
};

/**
 * Saves and restores Bacanagol progress as one string.
 *
 * Save receives the progress value and stores `game.progress`.
 * Load receives no arguments and replies on `progress.loadResult` with that string.
 *
 * @param {object} options
 * @param {string} options.storageKey - Local storage namespace for this landing.
 * @param {Function} options.sendMessage - Bridged Unity sendMessage.
 * @param {Function} options.addEventListener - Bridged Unity addEventListener.
 * @param {Function} options.removeEventListener - Bridged Unity removeEventListener.
 */
export const useGameProgressValue = ({
    storageKey,
    sendMessage,
    addEventListener,
    removeEventListener,
}) => {
    const handleSaveGameProgress = useCallback(
        (saveGameProgress) => {
            setStorageValue(storageKey, 'game.progress', saveGameProgress);
        },
        [storageKey]
    );

    const handleSendGameProgress = useCallback(() => {
        const dataProgress = getStorageValue(storageKey, 'game.progress') || '';

        sendMessage('progress.loadResult', dataProgress);
    }, [sendMessage, storageKey]);

    useGameProgressSlots({
        addEventListener,
        removeEventListener,
        onSave: handleSaveGameProgress,
        onLoad: handleSendGameProgress,
    });
};
